/* eslint-disable react/require-default-props */
/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { FormController } from '../form/FormController';
import { FormLabel } from '../form/FormLabel';
import { FormHelpMessage } from '../form/FormHelpMessage';
import { FormStatusMessage } from '../form/FormStatusMessage';
import { Input } from '../input/Input';
import useId from '../private/hooks/useId';

const TextField = (props) => {
  const {
    autoComplete,
    defaultValue,
    disabled,
    fullWidth,
    id: idProp,
    label,
    helpMessage,
    optional,
    onChange,
    placeholder,
    required,
    statusMessage,
    status,
    inputProps,
    type,
    value
  } = props;

  const id = useId(idProp);
  const formLabelId = label && id ? `${id}-label` : undefined;
  const helpMessageId = helpMessage && id ? `${id}-help-message` : undefined;
  const statusMessageId = statusMessage && id ? `${id}-status-message` : undefined;
  const ariaDescribedby = [];
  if (statusMessageId) ariaDescribedby.push(statusMessageId);
  if (helpMessageId) ariaDescribedby.push(helpMessageId);
  return (
    <FormController
      disabled={disabled}
      status={status}
      fullWidth={fullWidth}
    >
      <FormLabel
        htmlFor={id}
        id={formLabelId}
        optional={optional}
        required={required}
      >
        {label}
      </FormLabel>
      <Input
        aria-describedby={ariaDescribedby.length ? ariaDescribedby.join(' ') : undefined}
        autoComplete={autoComplete}
        defaultValue={defaultValue}
        id={id}
        placeholder={placeholder}
        type={type}
        value={value}
        onChange={onChange}
        {...inputProps}
      />
      {statusMessage && (
        <FormStatusMessage id={statusMessageId}>
          {statusMessage}
        </FormStatusMessage>
      )}
      {helpMessage && (
        <FormHelpMessage id={helpMessageId}>
          {helpMessage}
        </FormHelpMessage>
      )}
    </FormController>
  );
};

TextField.displayName = 'TextField';

TextField.propTypes = {
  /**
   * If "on", when a user starts to type in a field, the browser should display options to fill
   * in the field, based on earlier typed values.
   */
  autoComplete: PropTypes.string,
  /**
   * The default value for the input element.
   */
  defaultValue: PropTypes.string,
  /**
   * If `true`, the component is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, width is 100%.
   */
  fullWidth: PropTypes.bool,
  /**
   * Adds an id attribute to the Input component
   * and a htmlFor attribute to the FormLabel component.
   */
  id: PropTypes.string,
  /**
   * The text for the FormLabel component.
   */
  label: PropTypes.string,
  /**
   * The description for the TextField component.
   */
  helpMessage: PropTypes.string,
  /**
   * If true, adds "(optional)" to the FormLabel component. Optional has a lower priority over required.
   */
  optional: PropTypes.bool,
  /**
   * Adds onChange into Input component.
   */
  onChange: PropTypes.func,
  /**
   * Adds placeholder into Input component.
   */
  placeholder: PropTypes.string,
  /**
   * If true, adds a "*" to the FormLabel component. Required has a higher priority over optional.
   */
  required: PropTypes.bool,
  /**
   * The states of validation for the TextField component.
   */
  status: PropTypes.oneOf(['error', 'success', 'warning']),
  /**
   * Adds a message when there is a status value.
   */
  statusMessage: PropTypes.string,
  /**
   * The prop values that will be added to the Input component.
   */
  inputProps: PropTypes.object,
  type: PropTypes.string,
  /**
   * The value for the Input component.
   */
  value: PropTypes.string
};

export { TextField };
